import { lireTout, ecrire, supprimer, neuf } from './base.js';
import { reconstruire, instantane } from '../livre/index.js';
import { mouvementsDuDevis } from '../livre/depuisDevis.js';

/**
 * Accès aux mouvements et reconstruction du livre.
 *
 * Les mouvements sont IMMUABLES (AD-10) : ce module n'expose aucune mise à
 * jour. Une erreur se corrige par un mouvement d'ajustement, jamais en
 * réécrivant le passé - c'est ce qui rend le livre auditable.
 */

export async function listerMouvements() {
  const liste = await lireTout('movements');
  // Chronologique inverse pour l'affichage. Le rejeu, lui, retrie par date
  // croissante de son côté : les deux ordres ne servent pas à la même chose.
  return liste.sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

/**
 * Le livre courant, toujours reconstruit depuis les mouvements.
 *
 * Jamais lu depuis un cache persistant : c'est la règle d'or du document 02.
 * Le coût est négligeable - quelques centaines d'opérations sur un tableau
 * déjà en mémoire - et le bénéfice est qu'aucune corruption d'affichage ne
 * peut fausser durablement les chiffres.
 */
export async function livreCourant() {
  return reconstruire(await lireTout('movements'));
}

/**
 * Enregistre une suite de mouvements en les datant et en figeant l'état du
 * livre après chacun.
 *
 * L'instantané est calculé ici, en rejouant tout depuis le début plutôt qu'en
 * partant du dernier connu : un mouvement antidaté s'insère alors au bon
 * endroit, et l'instantané reste cohérent avec ce que donnerait un rejeu
 * complet.
 */
export async function enregistrerMouvements(brouillons, { date, note = '', quoteId = null } = {}) {
  const horodatage = date ?? new Date().toISOString();
  const poses = [];

  for (const b of brouillons) {
    const m = neuf({ ...b, date: horodatage, note, quoteId, snapshotApres: null });
    await ecrire('movements', m);
    poses.push(m);
  }

  // Une fois tous écrits, on rejoue et on fige les instantanés. Les calculer
  // au fil de l'écriture donnerait des valeurs fausses si un mouvement
  // antérieur existait déjà à la même date.
  const { journal } = await livreCourant();
  for (const { mouvement, snapshotApres } of journal) {
    if (!poses.some((m) => m.id === mouvement.id)) continue;
    await ecrire('movements', { ...mouvement, snapshotApres });
  }

  return poses.map((m) => m.id);
}

/** Confirme un devis : le décompose en mouvements et les enregistre. */
export async function confirmerDevis(devis, corridor) {
  const brouillons = mouvementsDuDevis({
    resultat: devis.result,
    rateOut: devis.profileSnapshot.rateOut,
    rateIn: devis.profileSnapshot.rateIn,
    corridor,
  });
  return enregistrerMouvements(brouillons, {
    note: devis.note || `Devis ${devis.id.slice(0, 8)}`,
    quoteId: devis.id,
  });
}

/**
 * Suppression logique d'un mouvement.
 *
 * Réservée à l'erreur de saisie manifeste - un mouvement créé par erreur, en
 * double. Corriger un montant faux passe par un ajustement, qui laisse les
 * deux traces.
 */
export async function supprimerMouvement(id) {
  await supprimer('movements', id);
  return listerMouvements();
}

export { instantane };
